import React, { useReducer, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  LOGIN_AND_CLOSE_URL,
  HTTP_RESPONSE_STATUS_FORBIDDEN_IN_THIS_MONTH,
} from "../../../config";
import { getLoginStatus } from "../../../api/sso";
import { getMetadata } from "../../../api/fdsnws";
import { setLoginStatus } from "../../../store/userSlice";
import { selectAllStations } from "../../../store/stationsSlice";
import {
  StationFilterContext,
  stationFilterDefaultState,
  stationFilterReducer,
  selectStations,
} from "../../../contexts/StationFilterContext";
import { StationSelectorFormSimple } from "../../forms/StationSelectorFormSimple";
import { LoadingScreen } from "../../LoadingScreen";
import { useTranslation } from "react-i18next";

const METADATA_LAST_UPDATED = "2024-11-01";

// const extractFileNameFromHeader = (response) => {
//   const disposition = response.headers['Content-Disposition']
//   if (disposition && disposition.indexOf('attachment') !== -1) {
//     return disposition.split('filename=')[1].split(';')[0]
//   }
// }

const getFileExtentionFromFormat = (format) => {
  if (format === "sacpz") {
    return "sacpz.txt";
  } else if (format === "text") {
    return "txt";
  }
  return format;
};

export const Metadata = () => {
  const stations = useSelector(selectAllStations);
  const isLoggedIn = useSelector((state) => state.user.login);
  const apiToken = useSelector((state) => state.user.apiToken);

  const [stationFilter, dispatchStationFilter] = useReducer(
    stationFilterReducer,
    stationFilterDefaultState
  );
  const [downloadFormat, setDownloadFormat] = useState("xml");
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const downloadMetadata = async (event) => {
    event.stopPropagation();

    let selectedStations = [];

    try {
      selectedStations = selectStations(stations, stationFilter);
    } catch (error) {
      alert(
        t("form.message.errorInStation") +
          "\r\n" +
          t("form.message." + error.message)
      );
      return;
    }
    if (selectedStations.length === 0) {
      alert(t("form.message.noStations"));
      return;
    }

    let apiFilter = {};

    if (stations.length !== selectedStations.length) {
      apiFilter.station = selectedStations.map(({ code }) => code);
    }

    apiFilter.format = downloadFormat;
    if (downloadFormat === "xml") {
      apiFilter.level = "response";
    }

    let logInStatus = {};
    if (!isLoggedIn) {
      setIsLoading(true);
      logInStatus = await getLoginStatus();
      if (logInStatus?.status === 200) {
        dispatch(setLoginStatus({ ...logInStatus }));
      }
      setIsLoading(false);
    }

    if (!isLoggedIn && logInStatus?.status !== 200) {
      if (window.confirm(t("form.message.clickDownloadAfterLogin"))) {
        const loginWindow = window.open(LOGIN_AND_CLOSE_URL, "_blank");
        if (loginWindow) {
          loginWindow.focus();
        }
      }
      return;
    }

    const _apiToken =
      logInStatus?.status === 200 ? logInStatus.token : apiToken;
    setIsLoading(true);
    const response = await getMetadata(apiFilter, _apiToken);
    if (response.status === 200) {
      // const fileName = extractFileNameFromHeader(response)
      const extention = getFileExtentionFromFormat(downloadFormat);
      const fileName = `kquake_station_metadata_${Date.now()}.${extention}`;
      const blob = await response.blob();
      setIsLoading(false);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.setAttribute("download", fileName);
      a.click();
    } else {
      setIsLoading(false);
      if (response.status === 204) {
        alert(
          t("form.message.noDataToDownload") +
            "\r\n" +
            t("form.message.contactAdministrator")
        );
      } else if (response.status === 401) {
        alert(t("form.message.authenticationFailedRefresh"));
      } else if (
        response.status === HTTP_RESPONSE_STATUS_FORBIDDEN_IN_THIS_MONTH
      ) {
        alert(t("form.message.downloadCapacityExceeded"));
      } else {
        const alertMessage = [
          t("form.message.failedToDownload"),
          `Error message:${response.statusText}`,
          t("form.message.contactAdministrator"),
        ];
        alert(alertMessage.join("\r\n"));
      }
    }
  };

  return (
    <div className="container">
      <div className="sub-container">
        {/* <div className="alert alert-danger"><i className="ion ion-md-information-circle-outline"></i> 로그인이 필요한 서비스입니다. </div> */}
        {/* <div style={{fontSize: "15px", marginBottom: "5px"}}><img src={Q_MARK_ICON} alt="question mark icon" />서비스 중인 지진관측자료(2016년 이후)의 메타데이터 다운로드 기능을 제공합니다.</div> */}
        <div style={{ fontSize: "15px", marginBottom: "5px" }}>
          &nbsp;&nbsp;<i className="ion ion-md-help-circle-outline"></i>
          {t("page.metadata.desc")}
        </div>
        <div className="overflow-auto">
          <table className="datatable">
            <colgroup>
              <col style={{ width: "20%" }} />
              <col style={{ width: "80%" }} />
            </colgroup>
            <tbody>
              <tr>
                <th className="first">{t("w.station")}</th>
                <td className="first">
                  <StationFilterContext.Provider
                    value={{
                      state: stationFilter,
                      dispatch: dispatchStationFilter,
                    }}
                  >
                    <StationSelectorFormSimple />
                  </StationFilterContext.Provider>
                </td>
              </tr>
              <tr>
                <th>{t("w.dataFormat")}</th>
                <td>
                  <form
                    action="#"
                    style={{ paddingTop: 10, paddingBottom: 10 }}
                  >
                    <label htmlFor="metadata.downloadFormat.xml">
                      <input
                        type="radio"
                        id="metadata.downloadFormat.xml"
                        className="with-gap"
                        checked={downloadFormat === "xml"}
                        onChange={(e) => {
                          if (e.target.checked) setDownloadFormat("xml");
                        }}
                      />
                      <span>StationXML</span>
                    </label>
                    &nbsp;&nbsp;&nbsp;&nbsp;
                    <label htmlFor="metadata.downloadFormat.sacpz">
                      <input
                        type="radio"
                        id="metadata.downloadFormat.sacpz"
                        className="with-gap"
                        checked={downloadFormat === "sacpz"}
                        onChange={(e) => {
                          if (e.target.checked) setDownloadFormat("sacpz");
                        }}
                      />
                      <span>SACPZ</span>
                    </label>
                    {/* &nbsp;&nbsp;&nbsp;&nbsp;
                    <label htmlFor="metadata.downloadFormat.text">
                      <input type="radio" id="metadata.downloadFormat.text" className="with-gap"
                        checked={downloadFormat==='text'}
                        onChange={e => {if (e.target.checked) setDownloadFormat("text")}}
                      />
                      <span>Text</span>
                    </label> */}
                    {/* <label>
                      <input className="with-gap" type="radio" checked={downloadFormat==='seed'} onChange={e => {if (e.target.checked) setDownloadFormat("seed")}} disabled />
                      <span><del>SEED</del></span>
                    </label>
                    <label>
                      <input className="with-gap" type="radio" checked={downloadFormat==='resp'} onChange={e => {if (e.target.checked) setDownloadFormat("resp")}} disabled />
                      <span><del>RESP File</del></span>
                    </label> */}
                    <div className="text-muted lh20 lheight29">
                      &nbsp;
                      <i className="ion ion-md-information-circle-outline"></i>
                      &nbsp;
                      {t("page.metadata.updated")}: {METADATA_LAST_UPDATED}
                    </div>
                    {/* <div className="text-muted lh20 lheight29">
                      &nbsp;<i className="ion ion-md-information-circle-outline"></i>&nbsp;
                      2022년 7월 22일 메타데이터 업데이트에는 오류 정정 내력이 포함되어 있습니다. <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;자세한 사항은 공지사항의 "[메타데이터] 오류 정정" 게시물을 참조하세요.
                    </div> */}
                  </form>
                </td>
              </tr>
            </tbody>
          </table>
          <br />
          <div className="center-align row">
            <div>
              <button
                type="submit"
                className="btn-block waves-effect waves-light button-lg"
                onClick={downloadMetadata}
              >
                {t("w.download")}
              </button>
            </div>
          </div>
        </div>
        <LoadingScreen screen={isLoading} />
      </div>
    </div>
  );
};
